import { DataRole, DataType, ExprCall } from '@mode-switch/express';
import { Attr } from '@mode-switch/model';
import { AvatarTypes, ModeHalResource, PaginationTypes } from '../..';
import { UserResource } from '../account/user.types';
import { TextFormat } from '../charts/field-format.types';
import { FieldColor } from '../charts/viz-format.types';

export interface DatasetDetails {
  name: string;
  description: string;
  editedAt: string;
  collection: {
    name: string;
    token: string;
    href: string;
  };
  creator: UserResource;
  lastSuccessfullyRunAt: string;
  reportImportsCount: number;
  hasEditAccess: boolean;
  thoughtspot?: ThoughtspotPublication;
  tsPublishMetaData?: TsPublishMetaData;
}

export interface DatasetResource extends ThoughtSpotPublicationResource {
  account_id: number;
  account_username: string;
  expected_runtime: number;
  collection_name: string;
  collection_token: string;
  created_at: string;
  published_at: string;
  description: string;
  drilldowns_enabled: boolean;
  drill_anywhere_enabled: boolean;
  edited_at: string;
  id: number;
  is_sample: boolean;
  last_run_at: string;
  last_saved_at: string;
  last_successful_run_token: string;
  last_successfully_run_at: string;
  last_successful_sync_at: string;
  manual_run_disabled: boolean;
  max_query_count: number;
  name: string;
  public: boolean;
  run_privately: boolean;
  space_token: string;
  token: string;
  updated_at: string;
  query_count: number;
  chart_count: number;
  schedules_count: number;
  runs_count: number;
  view_count: number;
}

export interface PublishedDatasetResource extends DatasetResource {
  thoughtspot_published_at: string;
  thoughtspot_publish_url: string;
  thoughtspot_publication_state: PublicationState;
  dataset_thoughtspot_publication_token: string;
}

export interface SwitchAttr {
  token: string;
  switchViewToken?: string;
  isLocal?: boolean;
  color?: FieldColor;
  format?: TextFormat;
  defaultsAggregate?: ExprCall.Name;

  name: string;
  desc?: string;
  source: Attr.Source;
  type: Attr.Type;
  role: Attr.Role;
  scale: Attr.Scale;
  formulaRole?: DataRole;
  formulaType: DataType;
  formulaSource: string;

  createdAt: string;
  updatedAt: string;
}

export interface DatasetBrowserData {
  accountId?: string;
  name: string;
  description: string;
  collection: string;
  lastRefreshed: string;
  creator: string;
  datasetToken: string;
  switchViewToken: string;
  isSampleDataset?: boolean;
  alreadyImported?: boolean;
  queryToken?: string;
  queryRunToken?: string;
  metaColumns?: [{ type: string; name: string; width: number; bytes: number }];
  switchAttrs?: SwitchAttr[];
}

export type ProducerType = ProducerTypes | null;

export enum ProducerTypes {
  SqlQuery = 'sql_query',
  GoogleSheet = 'google_sheet',
  NotebookCell = 'notebook_cell',
}

export interface DatasetUsage {
  name: string;
  creator: string;
  webLink: string;
  isPrivate?: boolean;
}

export interface DatasetUsageResult {
  pagination: PaginationTypes.Pagination;
  reports: DatasetUsage[];
}

export interface ThoughtspotDatasetPublisher {
  avatar: AvatarTypes.AvatarInitials;
  email: string;
  name: string;
  token: string;
  username: string;
}

export enum PublicationState {
  Published = 'published',
  Failed = 'failed',
  Submitted = 'submitted',
  Unknown = 'unknown',
}

export interface ThoughtspotPublication {
  publishedAt: string;
  publisher: ThoughtspotDatasetPublisher['name'];
  publisherUsername: ThoughtspotDatasetPublisher['username'];
  publicationState?: PublicationState;
  // TODO: this URL will be pointing to specific dataset page in data workspace
  // TODO: for now, it is pointing to the data workspace table list page
  publishUrl?: string;
  datasetThoughtspotPublicationToken?: string;
}

export interface TsPublishMetaData {
  schemaName: string;
  tableName: string;
}

export interface ThoughtspotDatasetPublicationResponse extends ThoughtspotPublication {
  publishUrl: string;
}

type ThoughtSpotPublicationResource = ModeHalResource & {
  _embedded: {
    thoughtspot_publisher: ThoughtspotDatasetPublisher;
  };
};

export enum BrowserDatasetType {
  Workspace = 'exclude_personal',
  Personal = 'in_personal',
}

export enum DatasetTabType {
  Workspace = 'workspace',
  Personal = 'personal',
}

export interface BrowserDatasetTabData {
  dataSources: DatasetBrowserData[];
  isLoading: boolean;
  searchTerm: string;
  searchEmptyState: boolean;
  pagination: PaginationTypes.Pagination;
  displayedColumns: string[];
}

export interface ExportedDatasetSourceResponseType {
  cell_content: string;
  execution_count: number;
}

export interface ExportedDatasetSourceContent {
  cellContent: string;
  executionCount: number;
}
